import { createContext, useContext, useEffect, useMemo, useState } from "react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const AuthContext = createContext(null);

const formatError = async (response) => {
  const data = await response.json().catch(() => ({}));
  if (typeof data.detail === "string") {
    return data.detail;
  }
  if (Array.isArray(data.detail)) {
    return data.detail.map((item) => item.msg).join(" ");
  }
  return "Something went wrong. Please try again.";
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    const refreshSession = async () => {
      const meResponse = await fetch(`${API}/auth/me`, { credentials: "include" });
      if (meResponse.ok) {
        setUser(await meResponse.json());
        return;
      }
      const refreshResponse = await fetch(`${API}/auth/refresh`, { method: "POST", credentials: "include" });
      if (refreshResponse.ok) {
        setUser(await refreshResponse.json());
        return;
      }
      setUser(null);
    };
    refreshSession()
      .catch(() => setUser(null))
      .finally(() => setChecking(false));
    const interval = window.setInterval(refreshSession, 12 * 60 * 1000);
    return () => window.clearInterval(interval);
  }, []);

  const signIn = async (email, password) => {
    const response = await fetch(`${API}/auth/login`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, password }),
    });
    if (!response.ok) {
      throw new Error(await formatError(response));
    }
    const nextUser = await response.json();
    setUser(nextUser);
    return nextUser;
  };

  const setAuthenticatedUser = (nextUser) => setUser(nextUser);

  const signOut = async () => {
    await fetch(`${API}/auth/logout`, { method: "POST", credentials: "include" });
    setUser(null);
  };

  const value = useMemo(() => ({ user, checking, signIn, signOut, setAuthenticatedUser, formatError }), [user, checking]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};